import h from 'hyperscript';

import copyToClipboardIcon from '@disy/cadenza-icons/copy-to-clipboard.svg';
import settingsIcon from '@disy/cadenza-icons/settings.svg';
import { createButton } from '@disy/ui/button';
import { copyToClipboard } from '@disy/ui/clipboard-button';
import { escapeAndMarkHTML } from '@disy/ui/escape-and-mark';
import { icon } from '@disy/ui/icon';
import { createMenuItemElement } from '@disy/ui/menu';
import '@disy/ui/menu.css';
import { KEEP_POPUP_OPEN_CLASS, closeAllPopups } from '@disy/ui/popup';
import { separateElements } from '@disy/ui/separator';

import { createPublicWorkbookUrl, createWorkbookSnapshot } from 'cadenza/api-client/workbook-management-api';
import { getStore } from 'cadenza/store/cadenza-store';
import type { WorkbookSnapshotCreationDto } from 'cadenza/workbook/api/workbook-snapshot-dto';
import {
  createPersonalInterimStatusNewButton,
  createPersonalInterimStatusTable,
} from 'cadenza/workbook/header/share-menu-interim-status-content/share-menu-interim-status-content';
import { openManageWorkbookDialog } from 'cadenza/workbook/manage-workbook-dialog/manage-workbook-dialog-util';
import { isAccessSettingsButtonVisible } from 'cadenza/workbook/state/workbook-header';
import { saveMapViewsExtentSilently } from 'cadenza/workbook/workbook-core-actions.state';
import {
  getActiveWorksheetId,
  getSnapshotDeletionPeriod,
  getWorkbookId,
  getWorkbookNameAndHash,
  getWorkbookPrintName,
  isWorkbookSaved,
  isWorkbookVersionPublishedWhenRepositoryPublishingProcessEnabled,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';

import './share-popup-content.css';
import i18n from './share-popup-content.properties';

export function createShareMenuContent() {
  return h(
    `.menu-content.share-menu-content.${KEEP_POPUP_OPEN_CLASS}.d-stack-v.space-2`,
    separateElements([
      isAccessSettingsButtonVisible(getStore().getState()) ? createAccessSettingsMenuContent() : undefined,
      createWorkbookLinkMenuContent(),
      isWorkbookFeatureAvailable(getStore().getState(), 'WORKBOOK_PERSONAL_SNAPSHOTS') ?
        createPersonalInterimStatusMenuContent()
      : undefined,
      isWorkbookFeatureAvailable(getStore().getState(), 'WORKBOOK_TEMPORARY_SNAPSHOTS') ?
        createTemporaryLinkMenuContent()
      : undefined,
    ]),
  );
}

function createAccessSettingsMenuContent() {
  const title = createTitle(i18n('accessSettings.title'));
  return h('.menu-content--item.d-stack-v', [
    title,
    createButton([icon(settingsIcon, { variant: 'normal' }), h('span', i18n('accessSettings.button'))], {
      title: i18n('accessSettings.button'),
      styleClass: 'd-menu-item',
      onclick: () => openManageWorkbookDialog('access'),
    }),
  ]);
}

function createWorkbookLinkMenuContent() {
  const title = createTitle(i18n('workbookLink.title'));
  const description = h('p', i18n('workbookLink.description'));
  return h(
    '.menu-content--item.d-stack-v',
    title,
    description,
    createButton([icon(copyToClipboardIcon, { variant: 'normal' }), h('span', i18n('workbookLink.button'))], {
      title: i18n('workbookLink.button'),
      styleClass: 'd-menu-item',
      onclick: () => copyWorkbookLinkToClipboard(),
    }),
  );
}

function copyWorkbookLinkToClipboard() {
  const workbookId = getWorkbookId(getStore().getState());
  const activeWorksheetId = getActiveWorksheetId(getStore().getState());
  copyToClipboard(new URL(createPublicWorkbookUrl(workbookId, activeWorksheetId), location.origin).toString(), {
    toast: i18n('workbookLink.copy.success'),
  });
  closeAllPopups();
}

function createTemporaryLinkMenuContent() {
  const title = createTitle(i18n('temporaryLink.title'));
  const description = h('p', {
    innerHTML: escapeAndMarkHTML(
      i18n('temporaryLink.description', { count: getSnapshotDeletionPeriod(getStore().getState()) }),
    ),
  });
  return h('.menu-content--item.d-stack-v', title, description, createTemporaryLinkButton());
}

function createTemporaryLinkButton() {
  return createButton([icon(copyToClipboardIcon, { variant: 'normal' }), h('span', i18n('temporaryLink.button'))], {
    title: i18n('temporaryLink.button'),
    styleClass: 'd-menu-item',
    disabled: !(
      isWorkbookSaved(getStore().getState()) &&
      isWorkbookVersionPublishedWhenRepositoryPublishingProcessEnabled(getStore().getState())
    ),
    onclick: async () => {
      await saveMapViewsExtentSilently();
      const currentWorkbookId = getWorkbookId(getStore().getState());
      const workbookSnapshotCreationDto: WorkbookSnapshotCreationDto = {
        workbookHash: getWorkbookNameAndHash(getStore().getState()).hash,
        worksheetId: getActiveWorksheetId(getStore().getState()),
        printName: getWorkbookPrintName(getStore().getState()),
        temporary: true,
      };
      const { workbookId, worksheetId } = await createWorkbookSnapshot(currentWorkbookId, workbookSnapshotCreationDto);
      copyToClipboard(new URL(createPublicWorkbookUrl(workbookId, worksheetId), location.origin).toString(), {
        toast: i18n('temporaryLink.copy.success'),
      });
      closeAllPopups();
    },
  });
}

function createPersonalInterimStatusMenuContent() {
  const table = createPersonalInterimStatusTable();
  return h(
    '.menu-content--item.d-stack-v',
    h('h2', i18n('personalInterimStatus.title')),
    h('p', i18n('personalInterimStatus.description')),
    createPersonalInterimStatusNewButton(() => table),
    table,
  );
}

function createTitle(title: string) {
  return h('h2', title);
}

export function getShareSubMenu() {
  return [
    createAccessSettingsMenuItem(),
    createCopyLinkMenuItem(),
    createPersonalInterimMenuItem(),
    createCurrentInterimMenuItem(),
  ];
}

function createAccessSettingsMenuItem() {
  if (isAccessSettingsButtonVisible(getStore().getState())) {
    return {
      label: i18n('accessSettings.title'),
      group: [
        {
          icon: icon(settingsIcon),
          label: i18n('accessSettings.button'),
          execute: () => openManageWorkbookDialog('access'),
        },
      ],
      separator: true,
    };
  }
}

function createCopyLinkMenuItem() {
  return {
    label: i18n('workbookLink.title'),
    group: [
      h('.share-menu-content--menu-text', i18n('workbookLink.description')),
      createMenuItemElement({
        icon: icon(copyToClipboardIcon),
        label: i18n('workbookLink.button'),
        execute: () => copyWorkbookLinkToClipboard(),
      }),
    ],
    separator: true,
  };
}

function createPersonalInterimMenuItem() {
  if (isWorkbookFeatureAvailable(getStore().getState(), 'WORKBOOK_PERSONAL_SNAPSHOTS')) {
    const table = createPersonalInterimStatusTable();
    return {
      label: i18n('personalInterimStatus.title'),
      group: [
        h('.share-menu-content--menu-text', i18n('personalInterimStatus.description')),
        createPersonalInterimStatusNewButton(() => table),
        table,
      ],
      separator: true,
    };
  }
}

function createCurrentInterimMenuItem() {
  if (isWorkbookFeatureAvailable(getStore().getState(), 'WORKBOOK_TEMPORARY_SNAPSHOTS')) {
    return {
      label: i18n('temporaryLink.title'),
      group: [
        h('.share-menu-content--menu-text', {
          innerHTML: escapeAndMarkHTML(
            i18n('temporaryLink.description', { count: getSnapshotDeletionPeriod(getStore().getState()) }),
          ),
        }),
        createTemporaryLinkButton(),
      ],
    };
  }
}
